import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './TrabajosPage.module.css';

const API_BASE = 'https://eduportalual.com/backend/api/x7Kp9mQ2';

function abrirArchivo(url) {
  const token = localStorage.getItem('token');
  window.open(url + (token ? '?token=' + encodeURIComponent(token) : ''), '_blank');
}

// Construye URL usando slug si está disponible, si no usa ID
function urlEntrega(entregaIdOSlug) {
  return `${API_BASE}/entregas/${entregaIdOSlug}/descargar`;
}

export default function TrabajosPage() {
  const { id: asigId } = useParams();
  const { state }      = useLocation();
  const { user }       = useAuth();
  const isProfesor     = user?.rol === 'profesor';

  const [trabajos, setTrabajos]         = useState([]);
  const [categorias, setCategorias]     = useState([]);
  const [entregasMap, setEntregasMap]   = useState({});
  const [califMap, setCalifMap]         = useState({}); // trabajoId -> calificacion
  const [selected, setSelected]         = useState(null);
  const [form, setForm]                 = useState({ titulo: '', descripcion: '', fecha_limite: '', nota_maxima: '10', categoria_id: '', peso_individual: '', tipo: 'entregable' });
  const [open, setOpen]                 = useState(false);
  const [showCatForm, setShowCatForm]   = useState(false);
  const [catForm, setCatForm]           = useState({ nombre: '', peso: '' });
  const [entregaModal, setEntregaModal] = useState(null);
  const [archivo, setArchivo]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [msgEntrega, setMsgEntrega]     = useState('');
  const [filtroCat, setFiltroCat]       = useState(null); // null = todas

  const load = () => {
    api.get(`/asignaturas/${asigId}/trabajos`)
      .then(async r => {
        const lista = r.data.data || [];
        setTrabajos(lista);
        if (!isProfesor && lista.length > 0) {
          const mapa = {};
          const califMapa = {};
          await Promise.all(lista.map(async t => {
            try {
              if (t.tipo === 'no_entregable') {
                // Para no entregables, cargar calificación directa
                try {
                  const cRes = await api.get(`/asignaturas/${asigId}/mis-calificaciones`);
                  const califs = cRes.data.data?.calificaciones || [];
                  const calif = califs.find(c => c.trabajo === t.titulo && c.nota !== null);
                  if (calif) califMapa[t.id] = calif;
                } catch {}
              } else {
                const res = await api.get(`/trabajos/${t.id}/entregas`);
                const entregas = res.data.data || [];
                if (entregas.length > 0) {
                  mapa[t.id] = entregas[0];
                  if (entregas[0].estado === 'calificado') {
                    try {
                      const cRes = await api.get(`/entregas/${entregas[0].id}/calificacion`);
                      if (cRes.data.data?.publicada == 1) califMapa[t.id] = cRes.data.data;
                    } catch {}
                  }
                }
              }
            } catch {}
          }));
          setEntregasMap(mapa);
          setCalifMap(califMapa);
        }
      })
      .finally(() => setLoading(false));
    api.get(`/asignaturas/${asigId}/categorias`).then(r => setCategorias(r.data.data || []));
  };

  useEffect(() => { load(); }, [asigId]);

  useEffect(() => {
    if (state?.trabajoId && trabajos.length > 0) {
      const encontrado = trabajos.find(t => t.id === parseInt(state.trabajoId));
      if (encontrado) setSelected(encontrado);
    }
  }, [trabajos, state]);

  const crearTrabajo = async e => {
    e.preventDefault();
    await api.post(`/asignaturas/${asigId}/trabajos`, {
      ...form,
      categoria_id:    form.categoria_id || null,
      peso_individual: form.peso_individual !== '' ? parseFloat(form.peso_individual) : null,
    });
    setOpen(false);
    setForm({ titulo: '', descripcion: '', fecha_limite: '', nota_maxima: '10', categoria_id: '', peso_individual: '', tipo: 'entregable' });
    load();
  };

  const crearCategoria = async e => {
    e.preventDefault();
    try {
      await api.post(`/asignaturas/${asigId}/categorias`, catForm);
      setCatForm({ nombre: '', peso: '' });
      setShowCatForm(false);
      load();
    } catch (err) { alert(err.response?.data?.error || 'Error al crear categoría'); }
  };

  const eliminarTrabajo = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Ocultar este trabajo?')) return;
    try {
      await api.post(`/trabajos/${id}`, { _method: 'PUT', visible: 0 });
      if (selected?.id === id) setSelected(null);
      load();
    } catch { alert('Error al ocultar'); }
  };

  const entregar = async e => {
    e.preventDefault(); setMsgEntrega('');
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      await api.post(`/trabajos/${entregaModal}/entregas`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsgEntrega('✓ Entrega realizada correctamente');
      setTimeout(() => { setEntregaModal(null); setMsgEntrega(''); load(); }, 1500);
    } catch (err) { setMsgEntrega(err.response?.data?.error || 'Error al entregar'); }
  };

  const vencido     = f => f && new Date(f) < new Date();
  const formatFecha = f => new Date(f).toLocaleDateString('es-ES', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  const sumaPesos = categorias.reduce((s, c) => s + parseFloat(c.peso), 0);

  // Filtrar trabajos
  const trabajosFiltrados = filtroCat === null
    ? trabajos
    : filtroCat === 'sin'
      ? trabajos.filter(t => !t.categoria_id)
      : trabajos.filter(t => t.categoria_id === filtroCat);

  // Agrupar por categoría
  const grupos = {};
  const sinCategoria = [];
  trabajosFiltrados.forEach(t => {
    if (t.categoria_id) {
      if (!grupos[t.categoria_id]) grupos[t.categoria_id] = { nombre: t.categoria_nombre, peso: t.categoria_peso, items: [] };
      grupos[t.categoria_id].items.push(t);
    } else { sinCategoria.push(t); }
  });

  // ── Vista detalle ─────────────────────────────────────────
  if (selected) {
    return (
      <div className={styles.page}>
        <div className={styles.detailTopBar}>
          <button className={styles.backBtn} onClick={() => setSelected(null)}>← Volver a trabajos</button>
          {isProfesor && (
            <button className={styles.btnEliminarDetalle} onClick={e => eliminarTrabajo(e, selected.id)}>
              🗑 Ocultar trabajo
            </button>
          )}
        </div>

        <article className={styles.detail}>
          <div className={styles.detailHeader}>
            <div>
              <div className={styles.detailBadges}>
                <span className={`${styles.estadoBadge} ${vencido(selected.fecha_limite) ? styles.estadoCerrado : styles.estadoAbierto}`}>
                  {vencido(selected.fecha_limite) ? '⛔ Cerrado' : '✅ Abierto'}
                </span>
                {selected.categoria_nombre && (
                  <span className={styles.categoriaBadge}>{selected.categoria_nombre}</span>
                )}
                {selected.peso_individual !== null && selected.peso_individual !== undefined && (
                  <span className={styles.pesoBadge}>⚖️ {parseFloat(selected.peso_individual).toFixed(2)}% nota final</span>
                )}
              </div>
              <h1 className={styles.detailTitle}>{selected.titulo}</h1>
            </div>
            {!isProfesor && selected.tipo !== 'no_entregable' && !vencido(selected.fecha_limite) && !entregasMap[selected.id] && (
              <button className={styles.btnEntregar} onClick={() => setEntregaModal(selected.id)}>
                📤 Entregar trabajo
              </button>
            )}
          </div>

          <div className={styles.detailMeta}>
            {selected.fecha_limite && (
              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>📅</span>
                <div>
                  <p className={styles.metaLabel}>Fecha límite</p>
                  <p className={`${styles.metaValue} ${vencido(selected.fecha_limite) ? styles.vencido : ''}`}>
                    {formatFecha(selected.fecha_limite)}
                  </p>
                </div>
              </div>
            )}
            <div className={styles.metaItem}>
              <span className={styles.metaIcon}>⭐</span>
              <div>
                <p className={styles.metaLabel}>Nota máxima</p>
                <p className={styles.metaValue}>{selected.nota_maxima} puntos</p>
              </div>
            </div>
            {selected.total_entregas !== undefined && isProfesor && (
              <div className={styles.metaItem}>
                <span className={styles.metaIcon}>📥</span>
                <div>
                  <p className={styles.metaLabel}>Entregas recibidas</p>
                  <p className={styles.metaValue}>{selected.total_entregas}</p>
                </div>
              </div>
            )}
          </div>

          <div className={styles.detailDivider} />

          {/* Calificación para trabajos no entregables */}
          {!isProfesor && selected.tipo === 'no_entregable' && (() => {
            const calif = califMap[selected.id];
            return (
              <div className={styles.entregaStatus}>
                {calif ? (
                  <div className={styles.califBox}>
                    <div className={styles.califHeader}>
                      <span className={styles.califTitulo}>📊 Calificación</span>
                      <div className={styles.califNotaCirculo}
                        style={{ '--nc': calif.nota >= 9 ? '#2d7a4f' : calif.nota >= 7 ? '#1a3a6b' : calif.nota >= 5 ? '#c07a1a' : '#c0392b' }}>
                        <span className={styles.califNotaNum}>{parseFloat(calif.nota).toFixed(1)}</span>
                      </div>
                    </div>
                    {calif.comentario && (
                      <div className={styles.califComentario}>
                        <p className={styles.califComentarioLabel}>💬 Comentario del profesor:</p>
                        <p className={styles.califComentarioTexto}>{calif.comentario}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.noEntregadoBox}>
                    <span className={styles.noEntregadoBadge}>⏳ Pendiente de calificación</span>
                  </div>
                )}
              </div>
            );
          })()}

          {!isProfesor && selected.tipo !== 'no_entregable' && (() => {
            const entrega = entregasMap[selected.id];
            const calif   = califMap[selected.id];
            return (
              <div className={styles.entregaStatus}>
                {entrega ? (
                  <div className={styles.entregadoBox}>
                    <div className={styles.entregadoHeader}>
                      <span className={styles.entregadoBadge}>✅ Entregado</span>
                      <span className={styles.entregadoFecha}>
                        {new Date(entrega.created_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </span>
                    </div>
                    {entrega.nombre_archivo && !entrega.nombre_archivo.includes('automatico') && (
                      <div className={styles.entregadoArchivo}>
                        <span className={styles.archivoIcon}>📄</span>
                        <span className={styles.archivoNombre}>{entrega.nombre_archivo}</span>
                        <button className={styles.btnVerArchivo}
                          onClick={() => abrirArchivo(`${API_BASE}/entregas/${entrega.slug || entrega.id}/descargar`)}>
                          👁 Ver mi entrega
                        </button>
                      </div>
                    )}

                    {/* Calificación publicada */}
                    {calif ? (
                      <div className={styles.califBox}>
                        <div className={styles.califHeader}>
                          <span className={styles.califTitulo}>📊 Calificación</span>
                          <div className={styles.califNotaCirculo}
                            style={{ '--nc': calif.nota >= 9 ? '#2d7a4f' : calif.nota >= 7 ? '#1a3a6b' : calif.nota >= 5 ? '#c07a1a' : '#c0392b' }}>
                            <span className={styles.califNotaNum}>{parseFloat(calif.nota).toFixed(1)}</span>
                          </div>
                        </div>
                        {calif.comentario && (
                          <div className={styles.califComentario}>
                            <p className={styles.califComentarioLabel}>💬 Comentario del profesor:</p>
                            <p className={styles.califComentarioTexto}>{calif.comentario}</p>
                          </div>
                        )}
                        {calif.nombre_correccion && (
                          <div className={styles.califCorreccion}>
                            <span className={styles.archivoIcon}>📎</span>
                            <span className={styles.archivoNombre}>{calif.nombre_correccion}</span>
                            <button className={styles.btnVerArchivo}
                              onClick={() => abrirArchivo(`${API_BASE}/calificaciones/${calif.id}/correccion`)}>
                              👁 Ver corrección
                            </button>
                          </div>
                        )}
                      </div>
                    ) : entrega.estado === 'calificado' ? (
                      <p className={styles.calificadoHint}>⏳ Calificación pendiente de publicación.</p>
                    ) : null}
                  </div>
                ) : (
                  <div className={styles.noEntregadoBox}>
                    <span className={styles.noEntregadoBadge}>📭 Pendiente de entrega</span>
                    {!vencido(selected.fecha_limite) && (
                      <p className={styles.noEntregadoHint}>Todavía puedes entregar antes de la fecha límite.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className={styles.detailDesc}>
            <h3 className={styles.descTitle}>Descripción</h3>
            {(selected.descripcion || '').split('\n').map((line, i) => <p key={i}>{line}</p>)}
          </div>
        </article>

        {entregaModal && (
          <div className={styles.overlay} onClick={() => setEntregaModal(null)}>
            <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
              <h3 className={styles.modalTitle}>Subir entrega</h3>
              <p className={styles.modalSub}>{selected.titulo}</p>
              <form onSubmit={entregar} className={styles.modalForm}>
                <label className={styles.label}>
                  Archivo (PDF, DOC, ZIP, PY, JAVA…)
                  <input type="file" accept=".pdf,.doc,.docx,.zip,.txt,.py,.java,.cpp"
                    onChange={e => setArchivo(e.target.files[0])} required />
                </label>
                {msgEntrega && <p className={msgEntrega.startsWith('✓') ? styles.success : styles.errorMsg}>{msgEntrega}</p>}
                <div className={styles.modalActions}>
                  <button type="button" className={styles.btnCancel} onClick={() => setEntregaModal(null)}>Cancelar</button>
                  <button type="submit" className={styles.btnGuardar}>Enviar entrega</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Vista lista ───────────────────────────────────────────
  return (
    <div className={styles.page}>

      {/* Acciones del profesor */}
      {isProfesor && (
        <div className={styles.profesorActions}>
          <button className={styles.btnNew} onClick={() => { setOpen(p => !p); setShowCatForm(false); }}>
            {open ? 'Cancelar' : '+ Crear trabajo'}
          </button>
          <button className={styles.btnNewCat} onClick={() => { setShowCatForm(p => !p); setOpen(false); }}>
            {showCatForm ? 'Cancelar' : '+ Nueva categoría'}
          </button>
          {categorias.length > 0 && (
            <span className={styles.sumaPesos}
              style={{ color: sumaPesos === 100 ? 'var(--success)' : 'var(--gold)' }}>
              Pesos: {sumaPesos.toFixed(0)}% / 100%
            </span>
          )}
        </div>
      )}

      {/* Filtros por categoría */}
      {categorias.length > 0 && (
        <div className={styles.filtrosCat}>
          <button
            className={`${styles.filtroCat} ${filtroCat === null ? styles.filtroCatActive : ''}`}
            onClick={() => setFiltroCat(null)}>
            Todos
          </button>
          {categorias.map(c => (
            <button key={c.id}
              className={`${styles.filtroCat} ${filtroCat === c.id ? styles.filtroCatActive : ''}`}
              onClick={() => setFiltroCat(filtroCat === c.id ? null : c.id)}>
              {c.nombre}
              <span className={styles.filtroCatPeso}>{parseFloat(c.peso).toFixed(0)}%</span>
              <span className={styles.filtroCatCount}>{c.total_trabajos} trabajos</span>
            </button>
          ))}
        </div>
      )}

      {/* Form nueva categoría */}
      {showCatForm && (
        <form onSubmit={crearCategoria} className={styles.form}>
          <h4 className={styles.formTitle}>Nueva categoría de evaluación</h4>
          <div className={styles.formRow}>
            <label className={styles.label}>
              Nombre
              <input className={styles.input} placeholder="ej: Prácticas, Examen Final..."
                value={catForm.nombre} onChange={e => setCatForm(p => ({ ...p, nombre: e.target.value }))} required />
            </label>
            <label className={styles.label}>
              Peso (%)
              <input className={styles.input} type="number" min="1" max="100" step="0.5"
                placeholder={`Disponible: ${(100 - sumaPesos).toFixed(0)}%`}
                value={catForm.peso} onChange={e => setCatForm(p => ({ ...p, peso: e.target.value }))} required />
            </label>
          </div>
          <button type="submit" className={styles.btnSubmit}>Crear categoría</button>
        </form>
      )}

      {/* Form nuevo trabajo */}
      {open && (
        <form onSubmit={crearTrabajo} className={styles.form}>
          <input className={styles.input} placeholder="Título" value={form.titulo}
            onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} required />
          <textarea className={styles.textarea} rows="3" placeholder="Descripción"
            value={form.descripcion} onChange={e => setForm(p => ({ ...p, descripcion: e.target.value }))} required />

          {/* Tipo de trabajo */}
          <div className={styles.tipoSelector}>
            <button type="button"
              className={`${styles.tipoBtn} ${form.tipo === 'entregable' ? styles.tipoBtnActive : ''}`}
              onClick={() => setForm(p => ({ ...p, tipo: 'entregable' }))}>
              📤 Entregable
            </button>
            <button type="button"
              className={`${styles.tipoBtn} ${form.tipo === 'no_entregable' ? styles.tipoBtnActive : ''}`}
              onClick={() => setForm(p => ({ ...p, tipo: 'no_entregable' }))}>
              📋 No entregable
            </button>
          </div>
          {form.tipo === 'no_entregable' && (
            <p className={styles.tipoHint}>
              ℹ️ El profesor calificará directamente a cada alumno sin necesidad de entrega ni fecha límite.
            </p>
          )}
          <div className={styles.formRow}>
            {form.tipo === 'entregable' && (
              <label className={styles.label}>
                Fecha límite
                <input className={styles.input} type="datetime-local" value={form.fecha_limite}
                  onChange={e => setForm(p => ({ ...p, fecha_limite: e.target.value }))} />
              </label>
            )}
            <label className={styles.label}>
              Nota máxima
              <input className={styles.input} type="number" min="0" max="10" step="0.5"
                value={form.nota_maxima} onChange={e => setForm(p => ({ ...p, nota_maxima: e.target.value }))} />
            </label>
          </div>
          {categorias.length > 0 && (
            <div className={styles.formRow}>
              <label className={styles.label}>
                Categoría
                <select className={styles.input} value={form.categoria_id}
                  onChange={e => setForm(p => ({ ...p, categoria_id: e.target.value }))}>
                  <option value="">Sin categoría</option>
                  {categorias.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} ({parseFloat(c.peso).toFixed(0)}%)</option>
                  ))}
                </select>
              </label>
              <label className={styles.label}>
                Peso individual (%)
                <span className={styles.labelHint}> — vacío = automático</span>
                <input className={styles.input} type="number" min="0" max="100" step="0.5"
                  placeholder="Auto"
                  value={form.peso_individual} onChange={e => setForm(p => ({ ...p, peso_individual: e.target.value }))} />
              </label>
            </div>
          )}
          <button type="submit" className={styles.btnSubmit}>Crear trabajo</button>
        </form>
      )}

      {loading && <p className={styles.empty}>Cargando trabajos...</p>}

      {!loading && trabajos.length === 0 && (
        <div className={styles.emptyState}>
          <p className={styles.emptyIcon}>📝</p>
          <p className={styles.emptyText}>No hay trabajos publicados todavía.</p>
        </div>
      )}

      {/* Lista agrupada */}
      <div className={styles.listWrapper}>
        {Object.entries(grupos).map(([catId, grupo]) => (
          <div key={catId} className={styles.categoriaGroup}>
            <div className={styles.categoriaGroupHeader}>
              <div className={styles.categoriaGroupLeft}>
                <span className={styles.categoriaGroupNombre}>{grupo.nombre}</span>
                <span className={styles.categoriaGroupPeso}>{parseFloat(grupo.peso).toFixed(0)}% de la nota final</span>
              </div>
              <span className={styles.categoriaGroupCount}>{grupo.items.length} trabajo{grupo.items.length !== 1 ? 's' : ''}</span>
            </div>
            <div className={styles.categoriaGroupItems}>
              {grupo.items.map(t => (
                <TarjetaTrabajo key={t.id} t={t} isProfesor={isProfesor}
                  entregasMap={entregasMap} vencido={vencido}
                  setSelected={setSelected} eliminarTrabajo={eliminarTrabajo} />
              ))}
            </div>
          </div>
        ))}

        {sinCategoria.length > 0 && (
          <div className={styles.categoriaGroup}>
            {Object.keys(grupos).length > 0 && (
              <div className={styles.categoriaGroupHeader}>
                <div className={styles.categoriaGroupLeft}>
                  <span className={styles.categoriaGroupNombre}>Sin categoría</span>
                </div>
              </div>
            )}
            <div className={styles.categoriaGroupItems}>
              {sinCategoria.map(t => (
                <TarjetaTrabajo key={t.id} t={t} isProfesor={isProfesor}
                  entregasMap={entregasMap} vencido={vencido}
                  setSelected={setSelected} eliminarTrabajo={eliminarTrabajo} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TarjetaTrabajo({ t, isProfesor, entregasMap, vencido, setSelected, eliminarTrabajo }) {
  const entrega = entregasMap[t.id];
  return (
    <div className={`${styles.card} ${vencido(t.fecha_limite) ? styles.cardCerrado : ''}`}
      onClick={() => setSelected(t)}>
      <div className={styles.cardBody}>
        <div className={styles.cardHeader}>
          <h4 className={styles.cardTitle}>{t.titulo}</h4>
          <div className={styles.cardBadges}>
            {!isProfesor && t.tipo !== 'no_entregable' && (
              entrega
                ? <span className={styles.badgeEntregado}>✅ Entregado</span>
                : <span className={styles.badgePendiente}>📭 Pendiente</span>
            )}
            {t.tipo === 'no_entregable' && (
              <span className={styles.badgeTipo}>📋 No entregable</span>
            )}
            {t.peso_individual !== null && t.peso_individual !== undefined && (
              <span className={styles.badgePeso}>⚖️ {parseFloat(t.peso_individual).toFixed(2)}%</span>
            )}
            <span className={`${styles.estadoBadge} ${vencido(t.fecha_limite) ? styles.estadoCerrado : styles.estadoAbierto}`}>
              {vencido(t.fecha_limite) ? 'Cerrado' : 'Abierto'}
            </span>
            {isProfesor && (
              <button className={styles.btnEliminarCard}
                onClick={e => eliminarTrabajo(e, t.id)} title="Ocultar trabajo">🗑</button>
            )}
          </div>
        </div>
        <p className={styles.cardDesc}>{t.descripcion}</p>
        <div className={styles.cardFooter}>
          {t.fecha_limite && (
            <span className={`${styles.fechaTag} ${vencido(t.fecha_limite) ? styles.vencido : ''}`}>
              📅 {vencido(t.fecha_limite) ? 'Venció' : 'Límite'}: {new Date(t.fecha_limite).toLocaleDateString('es-ES')}
            </span>
          )}
          <span className={styles.notaTag}>⭐ {t.nota_maxima} pts</span>
          {isProfesor && <span className={styles.entregasTag}>📥 {t.total_entregas} entregas</span>}
        </div>
      </div>
      <div className={styles.cardArrow}>›</div>
    </div>
  );
}
